import {useCallback, useEffect, useMemo, useState} from 'react';
import type {
  SourceEntityRecord,
  SourceEntityRequest,
} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';
import {HelpText} from 'src/design/atoms/HelpText';
import {Textbox} from 'src/design/atoms/Textbox';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useOfflinePersonnelAvailable} from 'src/offline/useOfflinePersonnelAvailable';
import {capitalize} from 'src/shared/utils/formatters';
import type {IFormFieldV2} from 'src/types/FormV2';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {useFormData} from '../FormDataContext';

const OFFLINE_NOTICE =
  'Offline — assignee lists are not cached on this device. Reconnect to change ' +
  'who this is assigned to.';

const OFFLINE_TABS_NOTICE =
  'Offline — only individual people are cached on this device. Departments, ' +
  'managers and groups are not available offline.';

import {Button, Input, Modal} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Space} from 'src/design/atoms/Space';
import {Text} from 'src/design/atoms/Text';
import {useDebouncedValue} from '@mantine/hooks';
import {IconAlertCircle} from '@tabler/icons-react';
import React from 'react';
import {DepartmentList} from './components/DepartmentList';
import {EmployeeList} from './components/EmployeeList';
import {OfflineEmployeeList} from './components/OfflineEmployeeList';
import {ManagerList} from './components/ManagerList';
import {ProjectList} from './components/ProjectList';
import {UserGroupList} from './components/UserGroupList';
import {VendorList} from './components/VendorList';
import {useSelectionWarning} from './hooks/useSelectionWarning';

export interface IEntitySelectOption {
  entityType: EntityTypeEnum;
  entityId?: string;
  id: string;
  label: string;
}

const optionKeys = [
  EntityTypeEnum.Employee,
  EntityTypeEnum.Manager,
  EntityTypeEnum.Department,
  EntityTypeEnum.Group,
];

const maxPageRows = 25;

export interface FormPersonnelSelectModalV2Props {
  field: IFormFieldV2;
  value: SourceEntityRequest;
  error?: string;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

export const FormPersonnelSelectModalV2 =
  React.memo<FormPersonnelSelectModalV2Props>(
    ({field, value, error, onChange}: FormPersonnelSelectModalV2Props) => {
      const formData = useFormData();
      const {isOffline} = useConnectivity();
      // Offline only the bundled people are selectable; without that section
      // nothing can be chosen at all.
      const offlinePersonnel = useOfflinePersonnelAvailable(isOffline);
      const offlineWithoutPersonnel = isOffline && offlinePersonnel !== true;

      // Get project ID from linked field if configured
      const projectId = useMemo(() => {
        const fieldName = field.personOptions?.projectFieldName;
        if (!fieldName) return undefined;
        const value = formData[fieldName];
        return value ? Number(value) : undefined;
      }, [field.personOptions?.projectFieldName, formData]);

      const [open, setOpen] = useState<boolean>(false);
      const hideSelectAll = useMemo(
        () => field.personOptions?.hideSelectAll ?? false,
        [field.personOptions?.hideSelectAll]
      );

      const [selectedOptionKey, setSelectedOptionKey] =
        useState<EntityTypeEnum>(EntityTypeEnum.Employee);

      const [selectedPersonnel, setSelectedPersonnel] = useState<
        SourceEntityRecord[]
      >([]);
      const [selectedManagers, setSelectedManagers] = useState<
        SourceEntityRecord[]
      >([]);
      const [selectedDepartments, setSelectedDepartments] = useState<
        SourceEntityRecord[]
      >([]);
      const [selectedUserGroups, setSelectedUserGroups] = useState<
        SourceEntityRecord[]
      >([]);

      // Opt-in extra audience dimensions (e.g. Vendor/Project/Attribute).
      // "Select all" and the 75%-selection warning apply only to the four
      // fixed entity types above, not to these.
      const additionalEntityTypes = useMemo(
        () => field.personOptions?.additionalEntityTypes ?? [],
        [field.personOptions?.additionalEntityTypes]
      );
      const allOptionKeys = useMemo(
        () => [...optionKeys, ...additionalEntityTypes],
        [additionalEntityTypes]
      );
      const [selectedExtra, setSelectedExtra] = useState<
        Partial<Record<EntityTypeEnum, SourceEntityRecord[]>>
      >({});
      const getExtraSelected = useCallback(
        (entityType: EntityTypeEnum) => selectedExtra[entityType] ?? [],
        [selectedExtra]
      );
      const makeExtraSetter = useCallback(
        (
            entityType: EntityTypeEnum
          ): React.Dispatch<React.SetStateAction<SourceEntityRecord[]>> =>
          action => {
            setSelectedExtra(prev => {
              const current = prev[entityType] ?? [];
              const next =
                typeof action === 'function'
                  ? (
                      action as (
                        prev: SourceEntityRecord[]
                      ) => SourceEntityRecord[]
                    )(current)
                  : action;
              return {...prev, [entityType]: next};
            });
          },
        []
      );

      const [searchTerm, setSearchTerm] = useState<string>('');
      const [showOnlySelected, setShowOnlySelected] = useState<boolean>(false);
      const [hasSelectedAll, setHasSelectedAll] = useState<boolean>(false);
      const [selectedAllType, setSelectedAllType] = useState<
        EntityTypeEnum | undefined
      >(undefined);

      const {
        handleEmployeeTotalCountChange,
        handleManagerTotalCountChange,
        handleDepartmentTotalCountChange,
        handleGroupTotalCountChange,
        selectionWarning,
        savedSelectionWarning,
      } = useSelectionWarning({
        selectedOptionKey,
        hasSelectedAll,
        selectionCounts: {
          employees: selectedPersonnel.length,
          managers: selectedManagers.length,
          departments: selectedDepartments.length,
          groups: selectedUserGroups.length,
        },
        savedValue: value,
      });

      const showSelectedAll = !hideSelectAll && hasSelectedAll;

      useEffect(() => {
        if (value && value.selectAllEntitiesType != selectedAllType) {
          setHasSelectedAll(!!value.selectAllEntitiesType);
          setSelectedAllType(value.selectAllEntitiesType);
          setSelectedOptionKey(
            value.selectAllEntitiesType ?? EntityTypeEnum.Employee
          );
        }
      }, [value?.selectAllEntitiesType]);

      const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 100);

      const handleCloseDialog = useCallback(() => {
        setOpen(false);
      }, [setOpen]);

      const clearSelections = useCallback(() => {
        setSelectedPersonnel([]);
        setSelectedManagers([]);
        setSelectedDepartments([]);
        setSelectedUserGroups([]);
        setSelectedExtra({});
      }, []);

      const handleCancelDialog = useCallback(() => {
        clearSelections();
        setOpen(false);
      }, [clearSelections, setOpen]);

      useEffect(() => {
        const departments = value?.entities?.filter(
          i => i.entityType === EntityTypeEnum.Department
        );

        const personnel = value?.entities?.filter(
          i => i.entityType === EntityTypeEnum.Employee
        );

        const managers = value?.entities?.filter(
          i => i.entityType === EntityTypeEnum.Manager
        );

        const groups = value?.entities?.filter(
          i => i.entityType === EntityTypeEnum.Group
        );

        setSelectedDepartments(departments ?? []);
        setSelectedManagers(managers ?? []);
        setSelectedPersonnel(personnel ?? []);
        setSelectedUserGroups(groups ?? []);

        if (additionalEntityTypes.length > 0) {
          const extra: Partial<Record<EntityTypeEnum, SourceEntityRecord[]>> =
            {};
          additionalEntityTypes.forEach(entityType => {
            extra[entityType] =
              value?.entities?.filter(i => i.entityType === entityType) ?? [];
          });
          setSelectedExtra(extra);
        }
      }, [value, additionalEntityTypes]);

      useEffect(() => {
        if (hasSelectedAll) {
          setSelectedAllType(selectedOptionKey);
          setSearchTerm('');
          clearSelections();
        } else {
          setSelectedAllType(undefined);
        }
      }, [hasSelectedAll, selectedOptionKey]);

      const getOutput = useCallback(() => {
        const departments = selectedDepartments.map(
          i =>
            ({
              entityId: i.entityId,
              entityType: EntityTypeEnum.Department,
            } as SourceEntityRecord)
        );

        const personnel = selectedPersonnel.map(
          i =>
            ({
              entityType: EntityTypeEnum.Employee,
              entityId: i.entityId,
            } as SourceEntityRecord)
        );

        const managers = selectedManagers.map(
          i =>
            ({
              entityId: i.entityId,
              entityType: EntityTypeEnum.Manager,
            } as SourceEntityRecord)
        );

        const groups = selectedUserGroups.map(
          i =>
            ({
              entityId: i.entityId,
              entityType: EntityTypeEnum.Group,
            } as SourceEntityRecord)
        );

        const extra = additionalEntityTypes.flatMap(
          entityType => selectedExtra[entityType] ?? []
        );

        return {
          selectAllEntitiesType: selectedAllType,
          entities: [
            ...departments,
            ...personnel,
            ...managers,
            ...groups,
            ...extra,
          ],
        } as SourceEntityRequest;
      }, [
        selectedDepartments,
        selectedPersonnel,
        selectedManagers,
        selectedUserGroups,
        selectedAllType,
        additionalEntityTypes,
        selectedExtra,
      ]);

      const handleOptionKeyClick = useCallback(
        (value: EntityTypeEnum) => {
          setSelectedOptionKey(value);
        },
        [setSelectedOptionKey]
      );

      const handleSearchTerm = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const {value} = e.target;
          setSearchTerm(value);
        },
        [setSearchTerm]
      );

      const getFormLabelText = useCallback(() => {
        if (selectedAllType === EntityTypeEnum.Department) {
          return 'Selected all Departments';
        }
        if (selectedAllType === EntityTypeEnum.Manager) {
          return 'Selected all Managers';
        }
        if (selectedAllType === EntityTypeEnum.Employee) {
          return 'Selected all Employees';
        }
        if (selectedAllType === EntityTypeEnum.Group) {
          return 'Selected all Groups';
        }

        return value?.entities && value?.entities.length > 0
          ? `Selected ${value.entities.length} selections.`
          : `No selections found`;
      }, [selectedAllType, value.entities]);

      const renderText = useCallback(
        (text: string) => <Text size="sm">{text}</Text>,
        []
      );

      const renderFormLabel = useCallback(
        () => renderText(getFormLabelText()),
        [getFormLabelText, renderText]
      );

      const handleSaveSelections = useCallback(() => {
        const output = getOutput();
        onChange(field.name, output, getFormLabelText());
        setOpen(false);
      }, [field.name, onChange, getOutput, getFormLabelText]);

      return (
        <Box data-testid={`form-field-${field.name}`}>
          <Input.Wrapper
            label={field.label}
            withAsterisk={field.required}
            error={error}
            description={field.description}>
            {renderFormLabel()}
            <Space mb="xs" />
            <Button
              size="sm"
              variant="outline"
              mb="md"
              color="black"
              disabled={field.disabled || offlineWithoutPersonnel}
              onClick={() => setOpen(true)}>
              Choose selections
            </Button>
            {offlineWithoutPersonnel ? <HelpText text={OFFLINE_NOTICE} /> : null}
          </Input.Wrapper>
          {savedSelectionWarning && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />} mb="md">
              <Text size="sm">{savedSelectionWarning}</Text>
            </Alert>
          )}

          <Modal
            opened={open}
            onClose={handleCloseDialog}
            title={field.label ?? 'Personnel'}
            centered
            zIndex={9999}
            size="lg">
            <Modal.Header
              pos={'sticky'}
              style={{paddingLeft: 0, paddingRight: 0}}>
              <Box w="100%">
                <Group gap="xs" wrap="wrap">
                  {allOptionKeys.length > 1 &&
                    allOptionKeys.map(item => (
                      <Button
                        key={item}
                        disabled={
                          (isOffline && item !== EntityTypeEnum.Employee) ||
                          (hasSelectedAll && selectedOptionKey !== item) ||
                          (searchTerm?.length > 0 && selectedOptionKey !== item)
                        }
                        size="sm"
                        variant={
                          selectedOptionKey === item ? 'filled' : 'outline'
                        }
                        color={selectedOptionKey === item ? 'yellow' : 'black'}
                        onClick={() => handleOptionKeyClick(item)}>
                        {capitalize(EntityTypeEnum[item].toString())}
                      </Button>
                    ))}
                </Group>
                {isOffline && <HelpText text={OFFLINE_TABS_NOTICE} />}
                <Group mt="md" justify="space-between">
                  {!hideSelectAll && optionKeys.includes(selectedOptionKey) && (
                    <CheckboxRow
                      label={`Select All ${EntityTypeEnum[selectedOptionKey]}s`}
                      checked={hasSelectedAll}
                      onChange={e => setHasSelectedAll(e.target.checked)}
                    />
                  )}
                  <CheckboxRow
                    label={`Show Only Selected`}
                    checked={showOnlySelected}
                    disabled={hasSelectedAll}
                    onChange={e => setShowOnlySelected(e.target.checked)}
                  />
                </Group>
                <Box mb="md">
                  <Textbox
                    label="Search"
                    name="search"
                    value={searchTerm}
                    onChangeCallback={handleSearchTerm}
                  />
                </Box>
                {selectionWarning && (
                  <Alert
                    color="yellow"
                    icon={<IconAlertCircle size={16} />}
                    mb="md">
                    <Text size="sm">{selectionWarning}</Text>
                  </Alert>
                )}
              </Box>
            </Modal.Header>
            {selectedOptionKey === EntityTypeEnum.Manager && (
              <ManagerList
                selected={selectedManagers}
                setSelected={setSelectedManagers}
                search={debouncedSearchTerm}
                showOnlySelected={showOnlySelected}
                showSelectedAll={showSelectedAll}
                itemsPerPage={maxPageRows}
                onTotalCountChange={handleManagerTotalCountChange}
                projectId={projectId}
              />
            )}
            {selectedOptionKey === EntityTypeEnum.Employee &&
              (isOffline ? (
                <OfflineEmployeeList
                  selected={selectedPersonnel}
                  setSelected={setSelectedPersonnel}
                  search={debouncedSearchTerm}
                  showOnlySelected={showOnlySelected}
                  showSelectedAll={showSelectedAll}
                  itemsPerPage={maxPageRows}
                />
              ) : (
                <EmployeeList
                  selected={selectedPersonnel}
                  setSelected={setSelectedPersonnel}
                  search={debouncedSearchTerm}
                  showOnlySelected={showOnlySelected}
                  showSelectedAll={showSelectedAll}
                  itemsPerPage={maxPageRows}
                  onTotalCountChange={handleEmployeeTotalCountChange}
                  projectId={projectId}
                />
              ))}
            {selectedOptionKey === EntityTypeEnum.Department && (
              <DepartmentList
                selected={selectedDepartments}
                setSelected={setSelectedDepartments}
                search={debouncedSearchTerm}
                showOnlySelected={showOnlySelected}
                showSelectedAll={showSelectedAll}
                itemsPerPage={maxPageRows}
                onTotalCountChange={handleDepartmentTotalCountChange}
              />
            )}
            {selectedOptionKey === EntityTypeEnum.Group && (
              <UserGroupList
                selected={selectedUserGroups}
                setSelected={setSelectedUserGroups}
                search={debouncedSearchTerm}
                showOnlySelected={showOnlySelected}
                showSelectedAll={showSelectedAll}
                itemsPerPage={maxPageRows}
                onTotalCountChange={handleGroupTotalCountChange}
                projectId={projectId}
              />
            )}
            {selectedOptionKey === EntityTypeEnum.Vendor && (
              <VendorList
                selected={getExtraSelected(EntityTypeEnum.Vendor)}
                setSelected={makeExtraSetter(EntityTypeEnum.Vendor)}
                search={debouncedSearchTerm}
                showOnlySelected={showOnlySelected}
                showSelectedAll={false}
                itemsPerPage={maxPageRows}
              />
            )}
            {selectedOptionKey === EntityTypeEnum.Project && (
              <ProjectList
                selected={getExtraSelected(EntityTypeEnum.Project)}
                setSelected={makeExtraSetter(EntityTypeEnum.Project)}
                search={debouncedSearchTerm}
                showOnlySelected={showOnlySelected}
                showSelectedAll={false}
                itemsPerPage={maxPageRows}
              />
            )}
            <Modal.Header pos={'sticky'} bottom={0}>
              <Group justify="end" w="100%">
                <Button
                  color="black"
                  variant="subtle"
                  size="sm"
                  onClick={handleCancelDialog}>
                  Cancel
                </Button>
                <Button onClick={handleSaveSelections} size="sm">
                  Save Selections
                </Button>
              </Group>
            </Modal.Header>
          </Modal>
        </Box>
      );
    }
  );
